import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StorageImage } from '@/components/ui/StorageImage';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  FileDown,
  Loader2,
  Phone,
  MapPin,
  Calendar,
  ClipboardList,
  FileText,
  ReceiptText,
  ChevronRight,
  Mail,
  Clock,
  CheckCircle2,
  UserCog,
  Wrench,
  PenLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import {
  useServiceOrder,
  useUpdateServiceOrder,
  useDeleteServiceOrder,
  useCreateServiceOrder,
  getOrderItems,
  getOrderPhotos,
  SERVICE_ORDER_STATUSES,
  type ServiceOrderStatus,
} from '@/hooks/useServiceOrders';
import { useClientContact } from '@/hooks/useClientContact';
import { SendMessageDialog } from '@/components/messages/SendMessageDialog';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { downloadQuotePDF, type PDFQuoteData } from '@/lib/pdfGenerator';
import { ThermalPrintButton, downloadThermalPdf } from '@/components/pdf/ThermalPrintButton';
import { DuplicateDocumentButton } from '@/components/documents/DuplicateDocumentButton';
import { buildServiceOrderDuplicate } from '@/lib/documentDuplication';
import { diagnosisEntries, diagnosisFromRow } from '@/lib/serviceOrderDiagnosis';
import { assetEntries, assetFromRow } from '@/lib/serviceOrderAsset';
import {
  buildServiceOrderSignatureColumns,
  serviceOrderSignatureFromRow,
} from '@/lib/serviceOrderHeader';
import { computeQuoteTotals } from '@/lib/quoteTotals';
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { formatPaymentMethods, normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import { quoteWarrantyFromRow, warrantyDisplayText } from '@/lib/quoteWarranty';
import { SignaturePad } from '@/components/quotes/SignaturePad';
import { GenerateReceiptButton } from '@/components/receipts/GenerateReceiptButton';
import { useWork } from '@/hooks/useWork';
import { WorkTrail } from '@/components/work/WorkTrail';
import { WorkGroupSection } from '@/components/work/WorkGroupSection';
import { workDocumentCount } from '@/lib/work';
import { StockDeductionPrompt } from '@/components/stock/StockDeductionPrompt';
import { StockAdjustmentPrompt } from '@/components/stock/StockAdjustmentPrompt';
import { StockReturnOnDeleteNotice } from '@/components/stock/StockReturnOnDeleteNotice';
import { useDocumentDeletionReturn } from '@/hooks/useDocumentDeletionReturn';
import { plural } from '@/lib/plural';
import { hexToRgb } from '@/lib/pdfTemplates/shared';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

/**
 * "10/08/2026 14:30" para colunas TIMESTAMP (`expected_date`, `completed_at`,
 * `client_signed_at`).
 *
 * ⚠️ Só para timestamp. `scheduled_date` é date-only guardado à meia-noite UTC e
 * precisa de `localDateFromIsoDatePart`, senão sai o dia anterior (#114/#54).
 */
const formatDateTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function ServiceOrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const noBrandingFooter = useFeature('no_branding_footer');
  const nfseFeature = useFeature('nfse_emission');
  const { isOnline } = useNetworkStatus();

  const { order, isLoading } = useServiceOrder(id);
  // O trabalho a que esta OS pertence. A âncora carrega o `quote_id` porque é
  // ele que leva à raiz — sem isso uma OS derivada viraria um trabalho solto.
  const { work, roots: workRoots, refetch: refetchWork } = useWork(
    order?.id ? { kind: 'service_order', id: order.id, quoteId: order.quote_id ?? null } : null,
  );
  // A OS guarda `client_phone` (snapshot); o cadastro é o fallback.
  const clientContact = useClientContact(order?.client_id);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const updateOrder = useUpdateServiceOrder();
  const deleteOrder = useDeleteServiceOrder();
  const createOrder = useCreateServiceOrder();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [generating, setGenerating] = useState(false);
  /** Traço em curso no quadro de assinatura (ainda não gravado). */
  const [signatureDraft, setSignatureDraft] = useState<string | null>(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);

  // A devolução de estoque na exclusão só pode ser oferecida ENQUANTO a OS
  // existe (`stock_movements.service_order_id` é SET NULL) — por isso a decisão
  // mora no diálogo de confirmação. O hook fica antes de qualquer `return`
  // porque a ordem dos hooks não pode variar entre renders.
  const deletionReturn = useDocumentDeletionReturn('service_order', id ? [id] : [], {
    enabled: confirmDelete,
  });

  if (!user) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/ordens-servico')} />
        <p className="py-16 text-center text-sm text-muted-foreground">Faça login para continuar.</p>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/ordens-servico')} />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ClipboardList className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">
              {isLoading ? 'Carregando…' : 'Ordem de serviço não encontrada.'}
            </p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/ordens-servico')}>
              Voltar
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  const items = getOrderItems(order);
  const photos = getOrderPhotos(order);
  const asset = assetEntries(assetFromRow(order as any));
  const clientSignature = serviceOrderSignatureFromRow(order as any);
  const warrantyText = warrantyDisplayText(quoteWarrantyFromRow(order as any));
  const acceptedMethods = formatPaymentMethods(order.payment_methods);
  // A decomposição da tela sai da MESMA função do PDF (#69): duas contas
  // divergiriam na primeira correção, e o cliente teria dois totais na mão.
  const totals = computeQuoteTotals({
    items: items as any[],
    discount: order.discount,
    discountType: order.discount_type,
    shipping: order.shipping,
  });

  const changeStatus = async (status: ServiceOrderStatus) => {
    try {
      await updateOrder.mutateAsync({
        id: order.id,
        updates: {
          status,
          completed_at: status === 'completed' ? new Date().toISOString() : null,
        },
      });
    } catch {
      // handled by mutation
    }
  };

  /**
   * Grava (ou apaga) a assinatura de recebimento do cliente.
   *
   * Vive na tela de DETALHE, não no formulário: quem assina é o cliente, na
   * entrega, com o aparelho na mão do prestador — não é um campo que se
   * preenche ao cadastrar a OS. É também por isso que as três colunas ficam
   * fora do payload do formulário: um `client_signature: null` numa edição
   * qualquer apagaria a prova de que o serviço foi entregue.
   */
  const saveSignature = async (signature: string | null) => {
    setSavingSignature(true);
    try {
      await updateOrder.mutateAsync({
        id: order.id,
        updates: buildServiceOrderSignatureColumns({
          signature,
          signerName: order.client_name ?? null,
          signedAt: null,
        }),
      });
      setSignatureDraft(null);
      setSignatureOpen(false);
      toast.success(signature ? 'Assinatura registrada.' : 'Assinatura removida.');
    } catch {
      // toast tratado na mutation
    } finally {
      setSavingSignature(false);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteOrder.mutateAsync({
        id: order.id,
        returnStock: deletionReturn.intent,
      });
      if (result?.returned) {
        toast.success(
          `${plural(result.returned.products, 'produto devolvido', 'produtos devolvidos')} ao estoque.`,
        );
      }
      toast.success('Ordem de serviço excluída.');
      navigate('/ordens-servico');
    } catch {
      // handled by mutation
    }
  };

  /** Fonte única do A4 e do cupom térmico desta OS. */
  const buildOrderPdfData = (): PDFQuoteData => {
    const company = activeCompany;
    return {
      documentType: 'ordem_servico',
      client: {
        name: order.client_name || 'Não informado',
        phone: order.client_phone || undefined,
        address: order.client_address || undefined,
        document: order.client_document || undefined,
      },
      company: company
        ? {
            name: company.name || 'Minha Empresa',
            cnpj: (company as any).cnpj_cpf || undefined,
            address: company.address || undefined,
            phone: company.phone || undefined,
            email: company.email || undefined,
            logoUrl: company.logo_url || undefined,
            pixKey: (company as any).pix_key || undefined,
            pixCity: (company as any).pix_city || undefined,
          }
        : undefined,
      quoteDate: order.created_at
        ? new Date(order.created_at).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      notes: order.description || undefined,
      // LEITURA do diagnóstico para o PDF. Sem esta linha o bloco existe no
      // banco e na tela, e o documento que o cliente recebe sai sem ele —
      // sem erro nenhum (mesma metade que o guard de escrita não cobre, #70).
      diagnosis: diagnosisFromRow(order as any),
      // Equipamento e responsáveis/prazos: mesma metade que o guard de escrita
      // não cobre (#70) — sem estas linhas o bloco existe no banco e na tela, e
      // o documento que o cliente recebe sai sem ele.
      serviceOrderAsset: assetFromRow(order as any),
      serviceOrderInfo: {
        attendantName: order.attendant_name ?? null,
        technicianName: order.technician_name ?? null,
        expectedDate: order.expected_date ?? null,
        completedAt: order.completed_at ?? null,
      },
      // Garantia, termos e formas aceitas: os templates já sabiam imprimir os
      // três (é o mesmo bloco do orçamento) — só faltava a OS preenchê-los.
      warranty: warrantyDisplayText(quoteWarrantyFromRow(order as any)) || undefined,
      termsConditions: order.terms_conditions || undefined,
      paymentMethods: normalizePaymentMethods(order.payment_methods),
      paymentTerms: order.payment_terms || undefined,
      // Os ajustes do valor DECOMPÕEM o total impresso. Sem eles o PDF mostraria
      // só a linha de Total e o cliente não veria o desconto que foi dado.
      discount: Number(order.discount ?? 0),
      discountType: order.discount_type === 'percent' ? 'percent' : 'value',
      shipping: Number(order.shipping ?? 0),
      items: items.map((it: any) => ({
        name: it.name,
        description: it.description,
        quantity: Number(it.quantity ?? 1),
        unit_price: Number(it.unit_price ?? 0),
        discount: Number(it.discount ?? 0),
        discount_type: (it.discount_type as 'value' | 'percent') || 'value',
        unit: it.unit,
        image_url: it.image_url,
      })),
      total: Number(order.total ?? 0),
      isPremium: noBrandingFooter.allowed,
      brandColorRGB: company ? hexToRgb((company as any).primary_color || '#10b981') : [16, 185, 129],
      template: (company as { pdf_template?: PDFQuoteData['template'] } | null)?.pdf_template || 'classico',
      sequenceId: order.sequence_id ?? undefined,
      showSequenceId: order.show_sequence_id ?? false,
      generalPhotos: photos.map((p: any) => ({
        imageUrl: p.image_url ?? p.imageUrl,
        title: p.title || undefined,
        description: p.description || undefined,
      })),
      // Assinatura salva do prestador (PRO); o nome que sai embaixo da linha é
      // o do TÉCNICO responsável, que é quem executou — a empresa já assina no
      // cabeçalho do documento.
      providerSignatureDataUrl: company?.signature_url || undefined,
      providerSignerName: order.technician_name || undefined,
      // Assinatura do CLIENTE, coletada na entrega. Ausente, a linha em branco
      // de sempre continua lá para assinar à mão.
      clientSignatureDataUrl: clientSignature.signature || undefined,
      clientSignerName: clientSignature.signerName || order.client_name || undefined,
      clientSignedAt: clientSignature.signedAt || undefined,
      logoWatermark: (company as any)?.logo_watermark === true,
    };
  };

  const clientSlug = (order.client_name || 'cliente').replace(/[^a-zA-Z0-9]/g, '_');

  const generatePDF = async () => {
    setGenerating(true);
    try {
      await downloadQuotePDF(
        buildOrderPdfData(),
        `ordem-de-servico-${clientSlug}-${Date.now()}.pdf`,
      );
    } catch (e) {
      console.error(e);
      toast.error('Não foi possível gerar o PDF.');
    } finally {
      setGenerating(false);
    }
  };

  const st = (order.status ?? 'pending') as ServiceOrderStatus;

  return (
    <MainLayout>
      <Header
        onBack={() => navigate('/ordens-servico')}
        action={
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/ordens-servico/${id}/editar`)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {/* Trilha do trabalho — substitui o link solto de "Origem: orçamento",
            que dizia de onde a OS veio mas não em que pé o serviço está. Só
            aparece quando há corrente: numa OS avulsa recém-criada ela desenharia
            quatro etapas vazias, e aí o link simples informa mais. */}
        {workDocumentCount(work) > 1 ? (
          <WorkTrail
            work={work!}
            anchorId={order.id}
            onOpenDocument={(route) => navigate(route)}
          >
            <WorkGroupSection roots={workRoots} onChanged={refetchWork} />
          </WorkTrail>
        ) : (
          <>
            {order.quote_id && !String(order.quote_id).startsWith('offline_') && (
              <button
                type="button"
                onClick={() => navigate(`/quotes/${order.quote_id}`)}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-dashed p-2.5 text-left text-sm transition hover:bg-muted"
              >
                <span className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  Origem: orçamento
                </span>
                <ChevronRight className="h-4 w-4 opacity-60" />
              </button>
            )}
            {/* Sem trilha (OS AVULSA) o accordion não existe — e é justamente
                aqui que agrupar vale mais: é o único jeito de prender esta OS a
                um trabalho. Por isso a ação fica solta neste ramo. */}
            <WorkGroupSection roots={workRoots} onChanged={refetchWork} />
          </>
        )}

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-lg">
                {order.title || order.client_name || 'Ordem de serviço'}
              </CardTitle>
              {order._offline_pending && <Badge variant="outline">Offline</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={st} onValueChange={(v) => changeStatus(v as ServiceOrderStatus)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_ORDER_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {order.client_name && (
              <div className="text-sm">
                <p className="font-medium">{order.client_name}</p>
                {order.client_phone && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {order.client_phone}
                  </p>
                )}
                {order.client_address && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {order.client_address}
                  </p>
                )}
              </div>
            )}

            {order.client_email && (
              <p className="flex items-center gap-1.5 break-all text-sm text-muted-foreground">
                <Mail className="h-3.5 w-3.5 shrink-0" /> {order.client_email}
              </p>
            )}

            {order.scheduled_date && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                {/* `scheduled_date` é date-only guardado à MEIA-NOITE UTC (é assim
                    que a Agenda o lê). `new Date(...).toLocaleDateString` daria o
                    DIA ANTERIOR em UTC-3 — a tela e a Agenda discordavam sobre o
                    mesmo campo (família #114/#54). */}
                Agendada para {localDateFromIsoDatePart(order.scheduled_date).toLocaleDateString('pt-BR')}
              </p>
            )}

            {order.expected_date && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Previsão de entrega: {formatDateTime(order.expected_date)}
              </p>
            )}

            {order.completed_at && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Encerrada em {formatDateTime(order.completed_at)}
              </p>
            )}

            {(order.attendant_name || order.technician_name) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {order.attendant_name && (
                  <span className="flex items-center gap-1.5">
                    <UserCog className="h-3.5 w-3.5 shrink-0" /> Atendente: {order.attendant_name}
                  </span>
                )}
                {order.technician_name && (
                  <span className="flex items-center gap-1.5">
                    <Wrench className="h-3.5 w-3.5 shrink-0" /> Técnico: {order.technician_name}
                  </span>
                )}
              </div>
            )}

            {order.description && <p className="text-sm">{order.description}</p>}
          </CardContent>
        </Card>

        {/* Equipamento / objeto — só aparece quando a OS tem o bloco. */}
        {asset.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Equipamento / objeto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {asset.map((entry) => (
                <div key={entry.key}>
                  <p className="text-xs font-medium text-muted-foreground">{entry.label}</p>
                  <p className="whitespace-pre-wrap break-words text-sm">{entry.text}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Diagnóstico técnico — só aparece quando a OS tem algum campo. */}
        {diagnosisEntries(diagnosisFromRow(order as any)).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Diagnóstico técnico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {diagnosisEntries(diagnosisFromRow(order as any)).map((entry) => (
                <div key={entry.key}>
                  <p className="text-xs font-medium text-muted-foreground">{entry.label}</p>
                  <p className="whitespace-pre-wrap text-sm">{entry.text}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {items.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Itens</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((it: any, i: number) => (
                <div key={it.id ?? i} className="flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate">{it.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {Number(it.quantity ?? 1)} × {formatCurrency(Number(it.unit_price ?? 0))}
                    </p>
                  </div>
                  <span className="shrink-0 font-medium">
                    {formatCurrency(Number(it.total ?? Number(it.quantity ?? 1) * Number(it.unit_price ?? 0)))}
                  </span>
                </div>
              ))}
              {/* Decomposição — só aparece quando há o que decompor, a mesma
                  regra do PDF, para os dois nunca discordarem. */}
              {(totals.descontoItens > 0 || totals.descontoCabecalho > 0 || totals.frete > 0) && (
                <div className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.descontoItens > 0 && (
                    <div className="flex justify-between">
                      <span>Descontos nos itens</span>
                      <span>- {formatCurrency(totals.descontoItens)}</span>
                    </div>
                  )}
                  {totals.descontoCabecalho > 0 && (
                    <div className="flex justify-between">
                      <span>Desconto no total</span>
                      <span>- {formatCurrency(totals.descontoCabecalho)}</span>
                    </div>
                  )}
                  {totals.frete > 0 && (
                    <div className="flex justify-between">
                      <span>Acréscimo / frete</span>
                      <span>+ {formatCurrency(totals.frete)}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
                <span>Total</span>
                <span>{formatCurrency(Number(order.total ?? 0))}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pagamento, garantia e condições — o bloco comercial do documento. */}
        {(acceptedMethods || order.payment_terms || warrantyText || order.terms_conditions) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Pagamento, garantia e condições</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {acceptedMethods && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Formas aceitas</p>
                  <p className="text-sm">{acceptedMethods}</p>
                </div>
              )}
              {order.payment_terms && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Condições de pagamento</p>
                  <p className="whitespace-pre-wrap text-sm">{order.payment_terms}</p>
                </div>
              )}
              {warrantyText && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Garantia</p>
                  <p className="text-sm">{warrantyText}</p>
                </div>
              )}
              {order.terms_conditions && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Termos e condições</p>
                  <p className="whitespace-pre-wrap text-sm">{order.terms_conditions}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {photos.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Fotos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p: any, i: number) => {
                  const url = p.image_url ?? p.imageUrl;
                  return url ? (
                    <StorageImage
                      key={p.id ?? i}
                      src={url}
                      className="aspect-square w-full rounded-md object-cover"
                    />
                  ) : null;
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/*
          ASSINATURA DO CLIENTE — coletada na ENTREGA, não no cadastro.
          É o que fecha o documento: sem ela, "o cliente recebeu" é palavra
          contra palavra. Fica antes do botão de PDF de propósito, para o
          documento gerado já sair assinado.
        */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Assinatura do cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {clientSignature.signature ? (
              <>
                <div className="rounded-md border bg-white p-2">
                  <img
                    src={clientSignature.signature}
                    alt="Assinatura do cliente"
                    className="mx-auto h-20 object-contain"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {clientSignature.signerName || order.client_name || 'Cliente'}
                  {clientSignature.signedAt ? ` · ${formatDateTime(clientSignature.signedAt)}` : ''}
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={savingSignature}
                  onClick={() => saveSignature(null)}
                >
                  Remover assinatura
                </Button>
              </>
            ) : signatureOpen ? (
              <>
                <SignaturePad
                  label="Peça ao cliente para assinar abaixo"
                  onSignatureChange={setSignatureDraft}
                />
                <div className="flex gap-2">
                  <Button3D
                    color="gray"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setSignatureDraft(null);
                      setSignatureOpen(false);
                    }}
                    // Cancelar NUNCA fica desabilitado junto com o confirmar
                    // (CLAUDE.md #41): com a gravação pendurada, o usuário
                    // ficaria preso no bloco sem saída.
                  >
                    Cancelar
                  </Button3D>
                  <Button3D
                    size="sm"
                    className="flex-1"
                    onClick={() => saveSignature(signatureDraft)}
                    disabled={!signatureDraft || savingSignature}
                  >
                    {savingSignature ? (
                      <>
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        Salvando…
                      </>
                    ) : (
                      'Salvar assinatura'
                    )}
                  </Button3D>
                </div>
              </>
            ) : (
              <Button variant="outline" className="w-full" onClick={() => setSignatureOpen(true)}>
                <PenLine className="mr-2 h-4 w-4" />
                Coletar assinatura na entrega
              </Button>
            )}
          </CardContent>
        </Card>

        <Button3D className="w-full" onClick={generatePDF} disabled={generating}>
          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          Gerar PDF
        </Button3D>

        {/* Comanda de bancada na entrada e via do cliente na entrega — os dois
            momentos em que a OS é impressa com alguém esperando. */}
        <ThermalPrintButton
          onPrint={(format) =>
            downloadThermalPdf(
              buildOrderPdfData(),
              `ordem-de-servico-${clientSlug}`,
              format,
            )
          }
        />

        {/* Serviço recorrente (manutenção mensal, revisão periódica): mesmo
            cliente, mesmos itens, execução nova. */}
        <DuplicateDocumentButton
          className="w-full"
          description="A cópia nasce PENDENTE, sem agendamento e sem o vínculo com o orçamento de origem — esse vínculo faz a OS ser pulada no custo do DRE (para não contar duas vezes), e herdado deixaria a cópia fora do custo para sempre. Diagnóstico e solução também não vêm: são as conclusões daquele reparo. Itens, fotos e o defeito relatado vêm."
          onDuplicate={async () => {
            const created = await createOrder.mutateAsync(
              buildServiceOrderDuplicate(order as any, items as any[], photos as any[]),
            );
            const newId = (created as { id?: string }).id ?? null;
            if (newId) {
              toast.success('Ordem de serviço duplicada.');
              navigate(`/ordens-servico/${newId}`);
            } else {
              // Offline a mutation enfileira e NÃO devolve id (a OS ainda nem
              // existe no servidor): navegar levaria a uma rota quebrada.
              toast.success('Cópia salva no aparelho e pronta para sincronizar.', { icon: '📴' });
              navigate('/ordens-servico');
            }
            return newId;
          }}
        />

        {/* Envio ao cliente. Não desabilita offline: os modelos ficam em cache e
            compor a mensagem é local. */}
        <Button3D color="whatsapp" className="w-full" onClick={() => setSendMessageOpen(true)}>
          <WhatsAppIcon className="mr-2 h-4 w-4" />
          Enviar por WhatsApp
        </Button3D>

        {/* Dar baixa no estoque — aparece quando a OS está concluída. Só produtos
            rastreados; recurso PRO; nunca dá baixa automático. */}
        {st === 'completed' && !String(order.id).startsWith('offline_') && (
          <StockDeductionPrompt
            items={items as any}
            serviceOrderId={order.id}
            /* Fecha o guard de cadeia: se o orçamento de origem já baixou esta
               venda, a OS não baixa de novo (ver StockDeductionPrompt). */
            originQuoteId={order.quote_id ?? null}
          />
        )}

        {/* Acerto por quantidade alterada. Diferente da baixa, NÃO depende do
            status: a quantidade pode mudar em qualquer momento depois que o
            estoque saiu, e é o movimento (não o status) que define se há o que
            acertar. */}
        {!String(order.id).startsWith('offline_') && (
          <StockAdjustmentPrompt
            items={items as any}
            documentUpdatedAt={(order as any).updated_at ?? null}
            serviceOrderId={order.id}
          />
        )}

        {/* Recibo do pagamento — aparece quando a OS está concluída. Nunca cria
            automático: abre o formulário pré-preenchido para o usuário confirmar. */}
        {st === 'completed' && (
          <GenerateReceiptButton
            className="w-full"
            prefill={{
              service_order_id: order.id,
              client_id: order.client_id ?? null,
              payer_name: order.client_name ?? null,
              payer_document: order.client_document ?? null,
              payer_address: order.client_address ?? null,
              amount: Number(order.total ?? 0),
              description: order.title || order.description || null,
            }}
          />
        )}

        {/* Emitir NFS-e — serviço concluído é o gatilho fiscal natural. Reaproveita
            a emissão a partir do orçamento de origem quando a OS veio de um. */}
        {st === 'completed' &&
          nfseFeature.allowed &&
          isOnline &&
          !String(order.id).startsWith('offline_') && (
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() =>
                navigate(
                  order.quote_id && !String(order.quote_id).startsWith('offline_')
                    ? `/nfse/nova?quote=${order.quote_id}`
                    : '/nfse/nova',
                )
              }
            >
              <span className="flex items-center">
                <ReceiptText className="mr-2 h-4 w-4" />
                Emitir NFS-e
              </span>
              <ChevronRight className="h-4 w-4 opacity-60" />
            </Button>
          )}
      </div>


      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir ordem de serviço?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A ordem de serviço e seus itens serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <StockReturnOnDeleteNotice state={deletionReturn} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Envio da OS ao cliente (modelo + variáveis + revisão) */}
      <SendMessageDialog
        open={sendMessageOpen}
        onOpenChange={setSendMessageOpen}
        category="service_order"
        context={{
          cliente: order.client_name ?? '',
          numero: order.sequence_id ?? '',
          titulo: order.title ?? '',
          // A coluna é `scheduled_date` (não `scheduled_at`); sem agendamento,
          // cai na data de criação.
          data: order.scheduled_date ?? order.created_at ?? null,
          empresa: activeCompany?.name ?? '',
        }}
        phone={order.client_phone || clientContact.phone}
        title="Enviar ordem de serviço"
      />
    </MainLayout>
  );
}

function Header({ onBack, action }: { onBack: () => void; action?: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3">
      <Button variant="ghost" size="icon" onClick={onBack}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <h1 className="flex-1 text-xl font-semibold">Ordem de serviço</h1>
      {action}
    </div>
  );
}
